import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  KeyRound,
  Lock,
  ShieldAlert,
  ShieldCheck,
  Users as UsersIcon,
} from "lucide-react";
import {
  getActiveSessions,
  getRecentErrors,
  getSecurityOverview,
} from "../../api/endpoints";
import type { ActiveSession, ErrorEntry, SecurityOverview } from "../../api/types";
import { Sparkline } from "../../components/Sparkline";
import { Btn } from "../../components/Btn";

// ─── mock fallback ─────────────────────────────────────────────────────────

const MOCK_SECURITY: SecurityOverview = {
  failed_logins_24h: 18,
  unauthorized_401: 84,
  forbidden_403: 11,
  unique_ips_24h: 412,
  top_failed_login_ips: [
    { ip: "10.0.4.18", failed_logins: 7 },
    { ip: "10.0.9.221", failed_logins: 4 },
    { ip: "10.0.3.17", failed_logins: 3 },
    { ip: "192.168.30.41", failed_logins: 2 },
    { ip: "10.0.4.55", failed_logins: 2 },
  ],
  window_hours: 24,
};

const MOCK_ERRORS: ErrorEntry[] = [
  { id: 9831, timestamp: new Date(Date.now() - 4 * 60_000).toISOString(),   method: "POST", path: "/api/v1/auth/login", status_code: 401, latency_ms: 12.4, user_login: null,         ip: "10.0.4.18", error_code: "UnauthorizedError" },
  { id: 9822, timestamp: new Date(Date.now() - 18 * 60_000).toISOString(),  method: "POST", path: "/api/v1/auth/login", status_code: 401, latency_ms: 9.0,  user_login: null,         ip: "10.0.9.221", error_code: "UnauthorizedError" },
  { id: 9810, timestamp: new Date(Date.now() - 33 * 60_000).toISOString(),  method: "GET",  path: "/api/v1/users",      status_code: 403, latency_ms: 6.2,  user_login: "kuznetsov.s", ip: "10.0.3.17", error_code: "ForbiddenError" },
  { id: 9778, timestamp: new Date(Date.now() - 102 * 60_000).toISOString(), method: "POST", path: "/api/v1/auth/login", status_code: 401, latency_ms: 11.2, user_login: null,         ip: "192.168.30.41", error_code: "UnauthorizedError" },
];

function useData<T>(qkey: string[], fn: () => Promise<T>, fallback: T) {
  const q = useQuery({ queryKey: qkey, queryFn: fn });
  return { value: q.isError ? fallback : (q.data ?? fallback), loading: q.isLoading };
}

function fmtRu(n: number): string {
  return n.toLocaleString("ru-RU");
}

function ago(seconds: number): string {
  if (seconds < 45) return "только что";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} мин назад`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} ч назад`;
  return `${Math.floor(seconds / 86400)} дн назад`;
}

// ─── page ──────────────────────────────────────────────────────────────────

export default function SecurityPage() {
  const sec = useData(["analytics", "security"], getSecurityOverview, MOCK_SECURITY);
  const errors = useData(["analytics", "errors", "security"], () => getRecentErrors(30), MOCK_ERRORS);
  const sessions = useData<ActiveSession[]>(["analytics", "sessions"], getActiveSessions, []);

  const s = sec.value;
  // только security-релевантные события: 401, 403, неуспешные логины
  const suspicious = errors.value.filter((e) => e.status_code === 401 || e.status_code === 403).slice(0, 12);

  return (
    <div className={sec.loading ? "opacity-90 transition-opacity" : "transition-opacity"}>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Безопасность
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            События доступа к Глобальному API за последние {s.window_hours} часа
          </div>
        </div>
        <Link to="/admin/keys">
          <Btn icon={KeyRound} variant="secondary">
            Ключи подписи
          </Btn>
        </Link>
      </header>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <SecCard
          icon={Lock}
          label="Неуспешных входов"
          value={fmtRu(s.failed_logins_24h)}
          sub="за 24 часа · /auth/login"
          tone={s.failed_logins_24h > 50 ? "danger" : s.failed_logins_24h > 10 ? "warning" : "success"}
          spark={[2, 3, 1, 4, 6, 5, 8, 4, 3, s.failed_logins_24h % 10]}
        />
        <SecCard
          icon={ShieldAlert}
          label="401 Unauthorized"
          value={fmtRu(s.unauthorized_401)}
          sub="несанкционированный доступ"
          tone={s.unauthorized_401 > 200 ? "danger" : s.unauthorized_401 > 50 ? "warning" : "success"}
          spark={[12, 14, 18, 20, 22, 24, 26, 30, 28, 24]}
        />
        <SecCard
          icon={ShieldCheck}
          label="403 Forbidden"
          value={fmtRu(s.forbidden_403)}
          sub="попытка доступа выше роли"
          tone={s.forbidden_403 > 30 ? "warning" : "success"}
          spark={[1, 2, 0, 3, 2, 4, 3, 5, 2, 3]}
        />
        <SecCard
          icon={UsersIcon}
          label="Уникальных IP"
          value={fmtRu(s.unique_ips_24h)}
          sub="источников трафика"
          tone="brand"
          spark={[280, 310, 340, 360, 380, 390, 400, 405, 408, s.unique_ips_24h]}
        />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* топ IP с failed logins */}
        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
            <span>Топ IP · неуспешные входы</span>
            <span>24 ч</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[400px] border-collapse text-[12.5px]">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                  <th className="px-3 py-2 font-medium">IP</th>
                  <th className="px-3 py-2 font-medium text-right">Failed</th>
                  <th className="px-3 py-2 font-medium text-right">Уровень</th>
                </tr>
              </thead>
              <tbody>
                {s.top_failed_login_ips.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center text-[12px] text-[var(--color-fg-muted)]">
                      Нет неуспешных входов в окне
                    </td>
                  </tr>
                )}
                {s.top_failed_login_ips.map((row) => {
                  const tone =
                    row.failed_logins >= 5 ? "danger"
                      : row.failed_logins >= 3 ? "warning"
                      : "info";
                  const toneCls =
                    tone === "danger"
                      ? "bg-[var(--color-danger-bg)] text-[var(--color-danger)]"
                      : tone === "warning"
                        ? "bg-[var(--color-warning-bg)] text-[var(--color-warning)]"
                        : "bg-[var(--color-surface-3)] text-[var(--color-brand-2)]";
                  return (
                    <tr key={row.ip} className="border-t border-[var(--color-border)]">
                      <td className="px-3 py-2 font-mono text-[12px] text-[var(--color-fg)]">{row.ip}</td>
                      <td className="px-3 py-2 text-right font-semibold text-[var(--color-fg)]">{row.failed_logins}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={"rounded-full px-2 py-0.5 text-[11px] font-medium " + toneCls}>
                          {tone === "danger" ? "высокий" : tone === "warning" ? "средний" : "низкий"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* недавние подозрительные события */}
        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h2 className="flex items-center gap-2 text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
            <AlertTriangle size={14} className="text-[var(--color-warning)]" />
            Последние подозрительные события
          </h2>
          <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
            401 / 403 — попытки без авторизации или выше роли
          </p>
          <ul className="mt-3 space-y-2">
            {suspicious.length === 0 && (
              <li className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 text-center text-[12.5px] text-[var(--color-fg-muted)]">
                Тихо — нарушений не зафиксировано.
              </li>
            )}
            {suspicious.map((e) => (
              <li
                key={e.id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-2.5 text-[12.5px]"
              >
                <span
                  className={
                    "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold " +
                    (e.status_code === 401
                      ? "bg-[var(--color-danger-bg)] text-[var(--color-danger)]"
                      : "bg-[var(--color-warning-bg)] text-[var(--color-warning)]")
                  }
                >
                  {e.status_code}
                </span>
                <span className="font-mono text-[11.5px] text-[var(--color-fg-muted)]">{e.method}</span>
                <span className="min-w-0 break-all font-mono text-[12px] text-[var(--color-fg)]">{e.path}</span>
                <span className="ml-auto text-[11px] text-[var(--color-fg-faint)]">
                  {e.ip ?? "—"}
                  {e.user_login && <> · {e.user_login}</>}
                  <> · {new Date(e.timestamp).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}</>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Ключи подписи — заголовок и кнопка */}
      <section className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[var(--color-success-bg)] text-[var(--color-success)]">
              <KeyRound size={18} />
            </span>
            <div>
              <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
                Ключи подписи
              </h2>
              <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
                Корпоративный ключ подписывает каждое приложение перед раздачей по локальным серверам.
                Ротация — отдельная страница.
              </p>
            </div>
          </div>
          <Link to="/admin/keys">
            <Btn icon={KeyRound} variant="primary">
              Перейти к ключам
            </Btn>
          </Link>
        </div>
      </section>

      {/* Активные сессии — по журналу запросов api_metrics */}
      <section className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div>
          <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
            Активные сессии
          </h2>
          <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
            Пользователи с активностью за последний час (по журналу запросов). JWT
            stateless — сессия определяется по паре «пользователь / IP».
          </p>
        </div>
        {sessions.loading ? (
          <p className="mt-3 text-[12px] text-[var(--color-fg-muted)]">Загрузка…</p>
        ) : sessions.value.length === 0 ? (
          <p className="mt-3 text-[12px] text-[var(--color-fg-muted)]">
            Нет активных сессий за последний час.
          </p>
        ) : (
          <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {sessions.value.map((row) => (
              <li
                key={row.login + (row.ip ?? "")}
                className="flex items-center justify-between rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 text-[12.5px]"
              >
                <div>
                  <div className="font-mono text-[12.5px] text-[var(--color-fg)]">{row.login}</div>
                  <div className="text-[11px] text-[var(--color-fg-muted)]">
                    {row.ip ?? "IP неизвестен"} · {fmtRu(row.requests)} запр.
                  </div>
                </div>
                <span className="text-[11px] text-[var(--color-fg-faint)]">
                  {ago(row.seconds_ago)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SecCard({
  icon: Icon,
  label,
  value,
  sub,
  tone,
  spark,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: string;
  sub: string;
  tone: "brand" | "success" | "warning" | "danger";
  spark: number[];
}) {
  const map = {
    brand: { fg: "text-[var(--color-fg)]", iconBg: "bg-[var(--color-surface-3)]", icon: "text-[var(--color-brand-2)]", sparkColor: "var(--color-brand-2)" },
    success: { fg: "text-[var(--color-fg)]", iconBg: "bg-[var(--color-success-bg)]", icon: "text-[var(--color-success)]", sparkColor: "var(--color-success)" },
    warning: { fg: "text-[var(--color-warning)]", iconBg: "bg-[var(--color-warning-bg)]", icon: "text-[var(--color-warning)]", sparkColor: "var(--color-warning)" },
    danger: { fg: "text-[var(--color-danger)]", iconBg: "bg-[var(--color-danger-bg)]", icon: "text-[var(--color-danger)]", sparkColor: "var(--color-danger)" },
  }[tone];

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-start justify-between">
        <div className="min-h-[26px] text-[10.5px] font-medium uppercase leading-snug tracking-[0.08em] text-[var(--color-fg-muted)]">
          {label}
        </div>
        <span className={"grid h-7 w-7 place-items-center rounded-lg " + map.iconBg}>
          <Icon size={14} className={map.icon} />
        </span>
      </div>
      <div className={"mt-2 text-[26px] font-semibold leading-none tracking-[-0.02em] " + map.fg}>
        {value}
      </div>
      <div className="mt-2 text-[11.5px] text-[var(--color-fg-muted)]">{sub}</div>
      <Sparkline values={spark} color={map.sparkColor} className="mt-3 h-7 w-full" />
    </div>
  );
}
